import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaUser, FaBook, FaCopy, FaCheck, FaChevronDown, FaChevronRight } from 'react-icons/fa';

const PasslyDocs = () => {
  const [copiedCode, setCopiedCode] = useState('');
  const [openSections, setOpenSections] = useState({
    installation: true,
    usage: true,
    core: false,
    passport: false,
    platform: false,
    analysis: false,
    utility: false,
    types: false
  });
  const [showScrollTop, setShowScrollTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 400) {
        setShowScrollTop(true);
      } else {
        setShowScrollTop(false);
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  const copyToClipboard = (text, id) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(''), 2000);
  };

  const toggleSection = (section) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const CodeBlock = ({ children, id, language = "javascript" }) => (
    <div className="relative bg-gray-950 rounded-lg border border-gray-800 overflow-hidden">
      {language && (
        <div className="flex items-center justify-between px-4 py-2 bg-gray-900 border-b border-gray-800">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
            <div className="w-3 h-3 rounded-full bg-green-500"></div>
          </div>
          <div className="text-xs text-gray-400 font-mono">{language}</div>
          <button
            onClick={() => copyToClipboard(children, id)}
            className="p-1 text-gray-400 hover:text-white transition-colors"
          >
            {copiedCode === id ? <FaCheck className="w-3 h-3" /> : <FaCopy className="w-3 h-3" />}
          </button>
        </div>
      )}
      <div className="p-4">
        <pre className="text-sm text-gray-200 font-mono leading-relaxed">
          <code>{children}</code>
        </pre>
      </div>
    </div>
  );

  const InstallBlock = ({ children, id }) => (
    <div className="relative bg-gray-950 rounded-lg border border-gray-800 overflow-hidden">
      <div className="flex items-center justify-between px-4 py-2 bg-gray-900 border-b border-gray-800">
        <div className="text-xs text-gray-400 font-mono">Terminal</div>
        <button
          onClick={() => copyToClipboard(children, id)}
          className="p-1 text-gray-400 hover:text-white transition-colors"
        >
          {copiedCode === id ? <FaCheck className="w-3 h-3" /> : <FaCopy className="w-3 h-3" />}
        </button>
      </div>
      <div className="p-4 bg-gray-950">
        <pre className="text-sm text-green-400 font-mono">
          <code>{children}</code>
        </pre>
      </div>
    </div>
  );

  const Section = ({ id, title, description, children, leftBorder = false }) => {
    const isOpen = openSections[id];
    
    return (
      <div className={`mb-8 ${leftBorder ? 'border-l-4 border-blue-500 pl-6' : ''}`}>
        <button
          onClick={() => toggleSection(id)}
          className="w-full flex items-center justify-between mb-4 text-left group bg-gradient-to-r from-gray-800/30 to-gray-700/30 backdrop-blur-sm rounded-lg p-4 border border-gray-600/20 hover:border-blue-500/40 hover:from-gray-700/40 hover:to-gray-600/40 transition-all duration-200 shadow-sm"
        >
          <div className="flex-1">
            <h2 className="text-xl font-semibold text-white group-hover:text-blue-400 transition-colors mb-1">
              {title}
            </h2>
            {description && (
              <p className="text-sm text-gray-400 group-hover:text-gray-300 transition-colors">
                {description}
              </p>
            )}
          </div>
          <div className="flex items-center space-x-2 ml-4">
            <div className={`px-3 py-1 rounded-md text-xs font-medium uppercase tracking-wide transition-all duration-200 ${
              isOpen 
                ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' 
                : 'bg-gray-600/30 text-gray-400 border border-gray-600/20 group-hover:bg-gray-500/40'
            }`}>
              {isOpen ? 'Expanded' : 'Collapsed'}
            </div>
            {isOpen ? (
              <FaChevronDown className="w-4 h-4 text-blue-400 transition-transform duration-200" />
            ) : (
              <FaChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-400 transition-colors" />
            )}
          </div>
        </button>
        {isOpen && (
          <div className="space-y-6 pl-4 border-l-2 border-blue-500/20">
            {children}
          </div>
        )}
      </div>
    );
  };

  const MethodCard = ({ method, description, params, returns, example }) => (
    <div className="bg-gray-800 bg-opacity-50 rounded-xl border border-gray-700 border-opacity-50 p-6 hover:border-blue-500 hover:border-opacity-50 hover:bg-opacity-70 transition-all duration-300 backdrop-blur-sm">
      <div className="mb-4">
        <code className="text-blue-400 font-mono text-lg font-bold tracking-wide">{method}</code>
      </div>
      <p className="text-gray-300 mb-6 leading-relaxed text-base">{description}</p>
      
      {params && params.length > 0 && (
        <div className="mb-6">
          <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Parameters</h4>
          <div className="space-y-3">
            {params.map((param, index) => (
              <div key={index} className="bg-gray-900 bg-opacity-70 rounded-lg border border-gray-700 border-opacity-50 p-4 hover:border-purple-500 hover:border-opacity-50 transition-colors">
                <code className="text-purple-400 text-sm font-mono font-medium">{param}</code>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {returns && (
        <div className="mb-6">
          <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Returns</h4>
          <div className="bg-gray-900 bg-opacity-70 rounded-lg border border-gray-700 border-opacity-50 p-4 hover:border-green-500 hover:border-opacity-50 transition-colors">
            <code className="text-green-400 text-sm font-mono font-medium">{returns}</code>
          </div>
        </div>
      )}
      
      {example && (
        <div>
          <h4 className="text-white font-bold mb-4 text-sm uppercase tracking-wider">Example</h4>
          <CodeBlock id={`example-${method.replace(/[^a-zA-Z0-9]/g, '')}`} language="javascript">
            {example}
          </CodeBlock>
        </div>
      )}
    </div>
  );

  const TypeCard = ({ name, definition }) => (
    <div className="bg-gray-800 bg-opacity-50 rounded-xl border border-gray-700 border-opacity-50 p-6 hover:border-blue-500 hover:border-opacity-50 hover:bg-opacity-70 transition-all duration-300 backdrop-blur-sm">
      <h3 className="text-xl font-bold mb-6 text-blue-400 tracking-wide">{name}</h3>
      <CodeBlock id={`type-${name.toLowerCase()}`} language="typescript">
        {definition}
      </CodeBlock>
    </div>
  );

  return (
    <div className="landing-page" style={{ background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)' }}>

<div className="landing-header">
  <div className="container landing-header-container">
    <div className="landing-logo-container">
                <Link to="/" className="landing-logo">
                  <span className="gradient-text">PASSLY</span>
                </Link>
      <span className="landing-header-beta-tag">BETA</span>
    </div>
    <div className="landing-buttons">
      <Link to="/app" className="landing-cta-button landing-header-button">
        <FaUser style={{ marginRight: '8px' }} />
        My Passport
      </Link>
      <Link to="/docs" className="landing-dev-button landing-header-button">
        <FaBook style={{ marginRight: '8px' }} />
        Developers
      </Link>
    </div>
  </div>
</div>


      <div className="min-h-screen text-white" style={{ paddingTop: '120px', paddingBottom: '80px' }}>
        <div className="container mx-auto px-6 max-w-4xl">
          
    
          <div className="mb-20 text-center">
            <button
              onClick={scrollToTop}
              className="text-6xl font-bold mb-8 bg-gradient-to-r from-blue-400 via-purple-400 to-blue-600 bg-clip-text text-transparent leading-tight hover:from-blue-300 hover:via-purple-300 hover:to-blue-500 transition-all duration-300 cursor-pointer border-none bg-transparent"
            >
              Passly SDK
            </button>
            <div className="max-w-2xl mx-auto">
              <p className="text-xl text-gray-300 leading-relaxed font-light">
                A powerful JavaScript SDK for integrating with Passly's decentralized identity protocol. 
                Build anti-sybil systems and verify user identities across social platforms.
              </p>
            </div>
          </div>

      
          <Section id="installation" title="Installation" description="Get started by installing the Passly SDK via npm" leftBorder>
            <div>
              <InstallBlock id="npm-install">npm i passly-sdk</InstallBlock>
            </div>
          </Section>

    
          <Section id="usage" title="Usage" description="Basic setup and initialization examples" leftBorder>
            <div>
              <div className="mb-6">
                <div className="flex items-center space-x-2 mb-3">
                  <div className="w-3 h-3 rounded-full bg-yellow-500"></div>
                  <span className="text-sm font-medium text-gray-300">JavaScript</span>
                </div>
                <CodeBlock id="basic-usage" language="javascript">
{`// Import and initialize Passly SDK
import PasslySDK from 'passly-sdk';

const passly = new PasslySDK();
await passly.connect();

// Check if user has a passport
const hasPassport = await passly.hasPassport('0x...');
console.log("User has passport:", hasPassport);`}
                </CodeBlock>
              </div>
            </div>
          </Section>

    
          <Section id="core" title="Core Methods" description="Essential methods for SDK initialization and connection">
            <div className="space-y-6">
              <MethodCard
                method="new PasslySDK()"
                description="Create a new PasslySDK instance. Simply instantiate with no parameters for the easiest setup."
                params={[]}
                returns="PasslySDK"
                example={`// Simple initialization - recommended approach
const passly = new PasslySDK();`}
              />

              <MethodCard
                method="connect()"
                description="Connect to the Passly contract. Must be called before using other methods."
                params={[]}
                returns="Promise<PasslySDK>"
                example={`// Connect to Passly
await passly.connect();

// Now you can use all SDK methods
const hasPassport = await passly.hasPassport('0x...');`}
              />
            </div>
          </Section>

        
          <Section id="passport" title="Passport Methods" description="Methods for retrieving and checking passport information">
            <div className="space-y-6">
              <MethodCard
                method="hasPassport(address)"
                description="Check if a wallet address has a passport."
                params={['address: string']}
                returns="Promise<boolean>"
                example={`const hasPassport = await passly.hasPassport('0x...');
console.log(hasPassport); // true or false`}
              />

              <MethodCard
                method="getPassport(address)"
                description="Get complete passport data including all verifications."
                params={['address: string']}
                returns="Promise<PassportData|null>"
                example={`const passport = await passly.getPassport('0x...');
if (passport) {
  console.log(passport.id);
  console.log(passport.verifications);
}`}
              />

              <MethodCard
                method="getPassportId(address)"
                description="Get the passport ID for a wallet address."
                params={['address: string']}
                returns="Promise<number|null>"
                example={`const passportId = await passly.getPassportId('0x...');
console.log(passportId); // 123 or null`}
              />
            </div>
          </Section>

      
          <Section id="platform" title="Platform Verification Methods" description="Methods for checking social platform verifications">
            <div className="space-y-6">
              <MethodCard
                method="isAccountVerified(platform, identifier)"
                description="Check if a specific social account is verified by any passport."
                params={[
                  'platform: string',
                  'identifier: string'
                ]}
                returns="Promise<{isVerified: boolean, passportId: number|null}>"
                example={`const result = await passly.isAccountVerified('twitter', 'username');
console.log(result.isVerified);
console.log(result.passportId);`}
              />

              <MethodCard
                method="hasVerifiedPlatform(address, platform)"
                description="Check if a user has verified a specific platform."
                params={[
                  'address: string',
                  'platform: string'
                ]}
                returns="Promise<boolean>"
                example={`const hasTwitter = await passly.hasVerifiedPlatform('0x...', 'twitter');
console.log(hasTwitter);`}
              />

              <MethodCard
                method="getPlatformIdentifier(address, platform)"
                description="Get a user's identifier on a specific platform."
                params={[
                  'address: string',
                  'platform: string'
                ]}
                returns="Promise<string|null>"
                example={`const handle = await passly.getPlatformIdentifier('0x...', 'twitter');
console.log(handle); // 'username' or null`}
              />
            </div>
          </Section>

     
          <Section id="analysis" title="Verification Analysis Methods" description="Methods for analyzing verification strength and account age">
            <div className="space-y-6">
              <MethodCard
                method="getVerificationStrength(address)"
                description="Calculate a verification strength score (0-100) for a user."
                params={['address: string']}
                returns="Promise<VerificationStrength|null>"
                example={`const strength = await passly.getVerificationStrength('0x...');
if (strength) {
  console.log(strength.score); // 0-100
  console.log(strength.grade); // 'A', 'B', 'C', 'D', 'F'
}`}
              />

              <MethodCard
                method="getVerificationAge(address, platform)"
                description="Get how long a user has been verified on a platform."
                params={[
                  'address: string',
                  'platform: string'
                ]}
                returns="Promise<VerificationAge|null>"
                example={`const age = await passly.getVerificationAge('0x...', 'twitter');
if (age) {
  console.log(age.ageInDays);
  console.log(age.humanReadable); // '5 days'
}`}
              />

              <MethodCard
                method="getEarliestVerification(address)"
                description="Get the earliest verification for a user (their 'account age' in the Passly ecosystem). Useful for determining how long a user has been part of the platform."
                params={['address: string']}
                returns="Promise<EarliestVerification|null>"
                example={`const earliest = await passly.getEarliestVerification('0x...');
if (earliest) {
  console.log(earliest.platform); // 'twitter'
  console.log(earliest.ageInDays); // 365
  console.log(earliest.humanReadable); // '365 days ago'
  console.log(earliest.verifiedAt); // Date object
}`}
              />
            </div>
          </Section>

       
          <Section id="utility" title="Utility Methods" description="Helper methods for platform support and proof verification">
            <div className="space-y-6">
              <MethodCard
                method="getSupportedPlatforms()"
                description="Get list of all supported platforms."
                params={[]}
                returns="Promise<string[]>"
                example={`const platforms = await passly.getSupportedPlatforms();
console.log(platforms); // ['twitter', 'github', 'discord', ...]`}
              />

              <MethodCard
                method="getProofHash(address, platform)"
                description="Get the proof hash for a specific platform verification."
                params={[
                  'address: string',
                  'platform: string'
                ]}
                returns="Promise<string|null>"
                example={`const hash = await passly.getProofHash('0x...', 'twitter');
console.log(hash); // '0x...' or null`}
              />

              <MethodCard
                method="getAllProofHashes(address)"
                description="Get all proof hashes for all verified platforms for a user. Returns an object mapping platform names to their respective proof hashes."
                params={['address: string']}
                returns="Promise<{[platform: string]: string}|null>"
                example={`const proofHashes = await passly.getAllProofHashes('0x...');
if (proofHashes) {
  console.log(proofHashes.twitter); // '0x...'
  console.log(proofHashes.github); // '0x...'
  console.log(proofHashes.discord); // '0x...'
}`}
              />
            </div>
          </Section>

    

        </div>
      </div>


      <footer style={{ 
        background: 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)',
        borderTop: '1px solid rgba(255, 255, 255, 0.1)',
        padding: '60px 0 40px 0'
      }}>
        <div className="container mx-auto px-6 text-center">
          <div className="mb-6">
            <span className="landing-logo">
              PASSLY
            </span>
          </div>
          <div className="text-gray-400 text-sm">
            © {new Date().getFullYear()} Passly Protocol. All rights reserved.
          </div>
        </div>
      </footer>

 
      <button 
        className={`fixed bottom-8 right-8 w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 z-50 flex items-center justify-center ${showScrollTop ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}
        onClick={scrollToTop}
        aria-label="Return to top"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5 text-white">
          <path d="M12 19V5M5 12l7-7 7 7"/>
        </svg>
      </button>
    </div>
  );
};

export default PasslyDocs;